/**
 * System metadata registry.
 * Each system defines its resolution, frame rate, button layout,
 * default key/gamepad mappings, and supported file extensions.
 */

import { SYSTEM_DISPLAY_ASPECT } from '../../shared/system-display-aspect.js';

/**
 * Systems that have a server-side headless render core (src/server/cores/).
 * For these, touch clients — which never capture a local WebM clip — ask the
 * server to render the milestone highlight from the replay log instead. Mirror
 * of the server registry (headless-core.js); extend in lockstep as cores land.
 * This is the always-on baseline; SNES and GBA are additionally server-renderable
 * when replay_render_snes / replay_render_gba are enabled (consumers check the
 * flags — see _serverRenderableSystem in main.js).
 */
export const SERVER_RENDER_SYSTEMS = ['nes', 'atari2600', 'genesis'];

// Solo libretro systems whose replays can be PLAYED via the JS-stepped
// lockstep path. SNES + Atari 2600 + Genesis + GBA verify server-side. Atari
// remains here for playback even though live recording now uses its safe
// self-driving input-log ring (SELF_DRIVING_RECORD_SYSTEMS below).
export const LOCKSTEP_SOLO_RECORD_SYSTEMS = new Set(['snes', 'atari2600', 'genesis', 'gba']);

// Solo systems that record via the passive Route-B input-log ring while the
// core SELF-DRIVES (no lockstep JS-stepping). The core stamps each consumed
// input frame; JS drains it into the recorder. GB/GBC was proven by
// verify-gb-selfdrive-record-spike.mjs; Atari was proven together with the
// in-loop state pump by verify-atari-selfdrive-checkpoint.mjs. Atari also stays
// in LOCKSTEP_SOLO_RECORD_SYSTEMS because live replay playback is stepped.
// N64 uses its own RPA1 ring because proportional axes are part of the input.
export const SELF_DRIVING_RECORD_SYSTEMS = new Set(['gb', 'atari2600', 'n64']);

// Systems whose replays play back live on a SELF-DRIVING core via the
// core-side playback buffer: decoded change-points
// are staged into the core and applied at the input read, frame-exact — the
// inverse of the record ring, proven by verify-gb-selfdrive-playback-spike.mjs
// (record == playback == headless verifier fingerprints). Playback on these
// systems is STEPLESS: no speed control, no scrub-stepping, no snapshot
// rewind (backward = restart-from-boot). Gated client-side by
// gb_replay_live_playback / n64_replays === true (fail-closed) in
// _isLivePlayable.
export const SELF_DRIVING_PLAYBACK_SYSTEMS = new Set(['gb', 'n64']);

// Lockstep cores built with the in-loop save pump (retroplay_pump_state). The pump
// was meant to let these IGNORE_INDIRECT cores snapshot safely from the replay loop
// — but it ALSO corrupts the Asyncify fiber in-browser (the #74 real-device
// regression: crash at the first snapshot boundary, frame 120), the same failure as
// the direct serialize. So browser snapshots are currently DISABLED for these cores
// (LockstepLibretroCore.canSnapshot returns false → rewind restarts from boot, the
// known-good #72 behaviour). The set + the pump exports + the ring's pump path all
// remain so a future, real-device-verified approach can re-enable them without a
// core rebuild. Self-driving play and headless verify are unaffected.
// See docs/superpowers/specs/2026-06-15-atari-genesis-pump-rewind.md.
export const SNAPSHOT_PUMP_SYSTEMS = new Set(['atari2600', 'genesis']);

// Systems whose SAVE STATE goes through the in-loop, rAF-polled async pump
// (saveStateN64 / retroplay_pump_state) instead of the synchronous direct
// serialize: N64 (always self-driving) plus the full-ASYNCIFY-rebuilt Atari/Genesis
// cores. The pump is only valid while emscripten drives the main loop, i.e. when the
// core is SELF-DRIVING — callers must gate on emu.selfDriving (a lockstep/recording
// instance has the loop cancelled and must fail save safe). Direct-serialize cores
// (jsnes NES, snes9x2010 SNES) are NOT here.
export const STATE_PUMP_SYSTEMS = new Set(['n64', 'atari2600', 'genesis']);

export function usesSnapshotPump(systemId, coreName = null) {
  // ClownMDEmu's browser build has safe direct serialize/unserialize while
  // externally stepped. Genesis Plus GX retains the legacy async pump guard.
  if (systemId === 'genesis' && coreName === 'clownmdemu') return false;
  return SNAPSHOT_PUMP_SYSTEMS.has(systemId);
}

export function usesStatePump(systemId, coreName = null) {
  // ClownMDEmu serializes directly only while externally stepped. In a
  // self-driving solo/hosted/fallback runtime, serialize must run from the
  // core's own loop through the async state pump like Genesis Plus GX.
  return STATE_PUMP_SYSTEMS.has(systemId);
}

export const NES_SYSTEM = {
  id: 'nes',
  name: 'NES',
  core: null, // uses jsnes (bundled), not a libretro core
  netplayMode: 'hosted',
  rollbackCapable: true,
  preferHosted: true,
  extensions: ['.nes'],
  width: 256,
  height: 240,
  fps: 60.0988,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.nes,
  maxPlayers: 2,
  buttons: {
    A:      0x01,
    B:      0x02,
    SELECT: 0x04,
    START:  0x08,
    UP:     0x10,
    DOWN:   0x20,
    LEFT:   0x40,
    RIGHT:  0x80,
  },
  keymap: {
    'KeyW':       0x10, // UP
    'KeyS':       0x20, // DOWN
    'KeyA':       0x40, // LEFT
    'KeyD':       0x80, // RIGHT
    'KeyJ':       0x01, // A
    'KeyK':       0x02, // B
    'Enter':      0x08, // START
    'ShiftRight': 0x04, // SELECT
    'ArrowUp':    0x10,
    'ArrowDown':  0x20,
    'ArrowLeft':  0x40,
    'ArrowRight': 0x80,
  },
  gamepadMap: {
    0:  0x01, // A / Cross
    1:  0x02, // B / Circle
    2:  0x02, // X / Square (alt B)
    3:  0x01, // Y / Triangle (alt A)
    8:  0x04, // Back / Share → SELECT
    9:  0x08, // Start / Options → START
    12: 0x10, // D-pad up
    13: 0x20, // D-pad down
    14: 0x40, // D-pad left
    15: 0x80, // D-pad right
  },
  controls: [
    { label: 'D-Pad', keys: 'W A S D / Arrows' },
    { label: 'A / B', keys: 'J / K' },
    { label: 'Start / Select', keys: 'Enter / R-Shift' },
    { label: 'Gamepad', keys: 'Auto-detected' },
  ],
};

// Explicit room-profile capability, imported only by multitap consumers.
export const SNES_MULTITAP_CAPABILITY = /* @__PURE__ */ Object.freeze({
  roomProfile: 'snes-multitap-v1', maxPlayers: 5,
});

export const SNES_SYSTEM = {
  id: 'snes',
  name: 'SNES',
  core: 'snes9x2010',
  netplayMode: 'hosted',
  extensions: ['.sfc', '.smc', '.zip'],
  width: 256,
  height: 224,
  fps: 60.0988,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.snes,
  maxPlayers: 2,
  buttons: {
    B:      0x001,
    Y:      0x002,
    SELECT: 0x004,
    START:  0x008,
    UP:     0x010,
    DOWN:   0x020,
    LEFT:   0x040,
    RIGHT:  0x080,
    A:      0x100,
    X:      0x200,
    L:      0x400,
    R:      0x800,
  },
  keymap: {
    'KeyW':       0x010, // UP
    'KeyS':       0x020, // DOWN
    'KeyA':       0x040, // LEFT
    'KeyD':       0x080, // RIGHT
    'KeyJ':       0x001, // B
    'KeyK':       0x100, // A
    'KeyU':       0x002, // Y
    'KeyI':       0x200, // X
    'KeyQ':       0x400, // L
    'KeyE':       0x800, // R
    'Enter':      0x008, // START
    'ShiftRight': 0x004, // SELECT
    'ArrowUp':    0x010,
    'ArrowDown':  0x020,
    'ArrowLeft':  0x040,
    'ArrowRight': 0x080,
  },
  gamepadMap: {
    0:  0x001, // A / Cross → B
    1:  0x100, // B / Circle → A
    2:  0x002, // X / Square → Y
    3:  0x200, // Y / Triangle → X
    4:  0x400, // LB → L
    5:  0x800, // RB → R
    8:  0x004, // Back / Share → SELECT
    9:  0x008, // Start / Options → START
    12: 0x010, // D-pad up
    13: 0x020, // D-pad down
    14: 0x040, // D-pad left
    15: 0x080, // D-pad right
  },
  controls: [
    { label: 'D-Pad', keys: 'W A S D / Arrows' },
    { label: 'B / A', keys: 'J / K' },
    { label: 'Y / X', keys: 'U / I' },
    { label: 'L / R', keys: 'Q / E' },
    { label: 'Start / Select', keys: 'Enter / R-Shift' },
    { label: 'Gamepad', keys: 'Auto-detected' },
  ],
};

export const GB_SYSTEM = {
  id: 'gb',
  name: 'Game Boy',
  core: 'gambatte',
  coreMultiplayer: 'gambatte_mp',
  netplayMode: 'hosted',
  linkCable: true,
  extensions: ['.gb', '.gbc', '.zip'],
  width: 160,
  height: 144,
  fps: 59.7275,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.gb,
  maxPlayers: 2,
  // The GB has no analog stick — on-screen touch controls default to the
  // d-pad (an explicit user choice still wins; see touchControlModeFor).
  touchControlDefault: 'dpad',
  // RetroPad id positions (bit k == RETRO_DEVICE_ID_JOYPAD k), matching SNES/
  // GBA. These bits feed LibretroCore's input override VERBATIM — the C side
  // returns (mask >> id) & 1 — so any other layout scrambles controls the
  // moment an override path (recording / lockstep / hosted injection) is
  // active. Until 2026-07-05 this map used the GB HARDWARE register order,
  // which rotated the D-pad 90° in recording-armed sessions while native
  // sessions felt fine (the iPad "inverted controls" incident). Guarded by
  // the RetroPad contract test in input.test.js.
  buttons: {
    B:      0x001,
    SELECT: 0x004,
    START:  0x008,
    UP:     0x010,
    DOWN:   0x020,
    LEFT:   0x040,
    RIGHT:  0x080,
    A:      0x100,
  },
  keymap: {
    'KeyW':       0x010, // UP
    'KeyS':       0x020, // DOWN
    'KeyA':       0x040, // LEFT
    'KeyD':       0x080, // RIGHT
    'KeyJ':       0x100, // A
    'KeyK':       0x001, // B
    'Enter':      0x008, // START
    'ShiftRight': 0x004, // SELECT
    'ArrowUp':    0x010,
    'ArrowDown':  0x020,
    'ArrowLeft':  0x040,
    'ArrowRight': 0x080,
  },
  gamepadMap: {
    0:  0x100, // A / Cross → A
    1:  0x001, // B / Circle → B
    8:  0x004, // Back / Share → SELECT
    9:  0x008, // Start / Options → START
    12: 0x010, // D-pad up
    13: 0x020, // D-pad down
    14: 0x040, // D-pad left
    15: 0x080, // D-pad right
  },
  controls: [
    { label: 'D-Pad', keys: 'W A S D / Arrows' },
    { label: 'A / B', keys: 'J / K' },
    { label: 'Start / Select', keys: 'Enter / R-Shift' },
    { label: 'Gamepad', keys: 'Auto-detected' },
  ],
};

export const GBA_SYSTEM = {
  id: 'gba',
  name: 'GBA',
  core: 'mgba',
  netplayMode: 'hosted',
  linkCable: true,
  extensions: ['.gba', '.zip'],
  width: 240,
  height: 160,
  fps: 59.7275,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.gba,
  maxPlayers: 2,
  // Bit values follow libretro RetroPad ID order (like SNES/Genesis), NOT the
  // GBA KEYINPUT register: link-cable play delivers this bitmask raw to the
  // core via _lockstep_set_input, which indexes it by RETRO_DEVICE_ID_JOYPAD_*.
  buttons: {
    B:      0x001,
    SELECT: 0x004,
    START:  0x008,
    UP:     0x010,
    DOWN:   0x020,
    LEFT:   0x040,
    RIGHT:  0x080,
    A:      0x100,
    L:      0x400,
    R:      0x800,
  },
  keymap: {
    'KeyW':       0x010, // UP
    'KeyS':       0x020, // DOWN
    'KeyA':       0x040, // LEFT
    'KeyD':       0x080, // RIGHT
    'KeyJ':       0x100, // A
    'KeyK':       0x001, // B
    'KeyQ':       0x400, // L
    'KeyE':       0x800, // R
    'Enter':      0x008, // START
    'ShiftRight': 0x004, // SELECT
    'ArrowUp':    0x010,
    'ArrowDown':  0x020,
    'ArrowLeft':  0x040,
    'ArrowRight': 0x080,
  },
  gamepadMap: {
    0:  0x100, // A / Cross → A
    1:  0x001, // B / Circle → B
    4:  0x400, // LB → L
    5:  0x800, // RB → R
    8:  0x004, // Back / Share → SELECT
    9:  0x008, // Start / Options → START
    12: 0x010, // D-pad up
    13: 0x020, // D-pad down
    14: 0x040, // D-pad left
    15: 0x080, // D-pad right
  },
  controls: [
    { label: 'D-Pad', keys: 'W A S D / Arrows' },
    { label: 'A / B', keys: 'J / K' },
    { label: 'L / R', keys: 'Q / E' },
    { label: 'Start / Select', keys: 'Enter / R-Shift' },
    { label: 'Gamepad', keys: 'Auto-detected' },
  ],
};

export const ATARI2600_SYSTEM = {
  id: 'atari2600',
  name: 'Atari 2600',
  core: 'stella2014',
  netplayMode: 'hosted',
  extensions: ['.a26', '.bin', '.zip'],
  width: 160,
  height: 192,
  fps: 59.922,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.atari2600,
  maxPlayers: 2,
  buttons: {
    B:      0x01, // Fire
    SELECT: 0x04,
    START:  0x08, // Reset
    UP:     0x10,
    DOWN:   0x20,
    LEFT:   0x40,
    RIGHT:  0x80,
  },
  keymap: {
    'KeyW':       0x10, // UP
    'KeyS':       0x20, // DOWN
    'KeyA':       0x40, // LEFT
    'KeyD':       0x80, // RIGHT
    'KeyJ':       0x01, // Fire
    'Enter':      0x08, // Reset
    'ShiftRight': 0x04, // SELECT
    'ArrowUp':    0x10,
    'ArrowDown':  0x20,
    'ArrowLeft':  0x40,
    'ArrowRight': 0x80,
  },
  gamepadMap: {
    0:  0x01, // A / Cross → Fire
    1:  0x01, // B / Circle → Fire (alt)
    8:  0x04, // Back / Share → SELECT
    9:  0x08, // Start / Options → Reset
    12: 0x10, // D-pad up
    13: 0x20, // D-pad down
    14: 0x40, // D-pad left
    15: 0x80, // D-pad right
  },
  controls: [
    { label: 'D-Pad', keys: 'W A S D / Arrows' },
    { label: 'Fire', keys: 'J' },
    { label: 'Reset / Select', keys: 'Enter / R-Shift' },
    { label: 'Gamepad', keys: 'Auto-detected' },
  ],
  touchLabels: {
    B: 'FIRE',
    START: 'RESET',
    SELECT: 'SELECT',
  },
};

export const C64_SYSTEM = {
  id: 'c64',
  name: 'Commodore 64',
  core: 'vice_x64',
  netplayMode: 'hosted',
  extensions: ['.d64', '.d71', '.d81', '.g64', '.prg', '.crt', '.t64', '.tap', '.zip'],
  diskExtensions: ['.d64', '.d71', '.d81', '.g64'],
  width: 384,
  height: 247,
  fps: 59.826,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.c64,
  maxPlayers: 2,
  buttons: {
    B:      0x01, // Fire
    UP:     0x10,
    DOWN:   0x20,
    LEFT:   0x40,
    RIGHT:  0x80,
  },
  keymap: {
    'KeyW':       0x10, // UP
    'KeyS':       0x20, // DOWN
    'KeyA':       0x40, // LEFT
    'KeyD':       0x80, // RIGHT
    'KeyJ':       0x01, // Fire
    'ArrowUp':    0x10,
    'ArrowDown':  0x20,
    'ArrowLeft':  0x40,
    'ArrowRight': 0x80,
  },
  gamepadMap: {
    0:  0x01, // A / Cross → Fire
    1:  0x01, // B / Circle → Fire (alt)
    12: 0x10, // D-pad up
    13: 0x20, // D-pad down
    14: 0x40, // D-pad left
    15: 0x80, // D-pad right
  },
  controls: [
    { label: 'Joystick', keys: 'Touch pad / Gamepad' },
    { label: 'Fire', keys: 'Touch B / Gamepad A or B' },
    { label: 'RUN/STOP', keys: 'Esc' },
    { label: 'RESTORE', keys: 'Fn+Up / Page Up' },
    { label: 'INST/DEL', keys: 'Backspace' },
    { label: 'Keyboard', keys: 'Always active → C64' },
  ],
  touchLabels: {
    B: 'FIRE',
  },
};

export const GENESIS_SYSTEM = {
  id: 'genesis',
  name: 'Genesis',
  core: 'genesis_plus_gx',
  netplayMode: 'hosted',
  extensions: ['.gen', '.md', '.bin', '.smd', '.zip'],
  width: 320,
  height: 224,
  fps: 59.9227,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.genesis,
  maxPlayers: 2,
  // Button names use RetroPad convention so P1/P2_KEY_MAP lookups in
  // libretro-core.js inject the correct keyboard keys during hosted netplay.
  // Genesis Plus GX maps: B→B, A→Y, C→A, X→X, Y→L, Z→R, Mode→SELECT.
  buttons: {
    B:      0x001, // Genesis B
    Y:      0x002, // Genesis A
    SELECT: 0x004, // Genesis Mode
    START:  0x008, // Genesis Start
    UP:     0x010,
    DOWN:   0x020,
    LEFT:   0x040,
    RIGHT:  0x080,
    A:      0x100, // Genesis C
    X:      0x200, // Genesis X
    L:      0x400, // Genesis Y
    R:      0x800, // Genesis Z
  },
  keymap: {
    'KeyW':       0x010, // UP
    'KeyS':       0x020, // DOWN
    'KeyA':       0x040, // LEFT
    'KeyD':       0x080, // RIGHT
    'KeyJ':       0x001, // B
    'KeyK':       0x100, // C
    'KeyU':       0x002, // A
    'KeyI':       0x200, // X
    'KeyQ':       0x400, // Y
    'KeyE':       0x800, // Z
    'Enter':      0x008, // START
    'ShiftRight': 0x004, // MODE
    'ArrowUp':    0x010,
    'ArrowDown':  0x020,
    'ArrowLeft':  0x040,
    'ArrowRight': 0x080,
  },
  gamepadMap: {
    0:  0x001, // A / Cross → B
    1:  0x100, // B / Circle → C
    2:  0x002, // X / Square → A
    3:  0x200, // Y / Triangle → X
    4:  0x400, // LB → Y
    5:  0x800, // RB → Z
    8:  0x004, // Back / Share → MODE
    9:  0x008, // Start / Options → START
    12: 0x010, // D-pad up
    13: 0x020, // D-pad down
    14: 0x040, // D-pad left
    15: 0x080, // D-pad right
  },
  controls: [
    { label: 'D-Pad', keys: 'W A S D / Arrows' },
    { label: 'A / B / C', keys: 'U / J / K' },
    { label: 'X / Y / Z', keys: 'I / Q / E' },
    { label: 'Start / Mode', keys: 'Enter / R-Shift' },
    { label: 'Gamepad', keys: 'Auto-detected' },
  ],
};

export const N64_SYSTEM = {
  id: 'n64',
  name: 'N64',
  core: 'mupen64plus_next',
  netplayMode: 'hosted',
  extensions: ['.z64', '.n64', '.v64', '.zip'],
  width: 320,
  height: 240,
  fps: 60,
  aspectRatio: SYSTEM_DISPLAY_ASPECT.n64,
  maxPlayers: 4,   // 4-player target; generic N-player room infra lands in Phase 2
  hasAnalog: true, // solo keyboard analog stick is wired (WASD); gamepad/netplay analog lands in Phase 3
  // Button names use RetroPad convention so P1/P2_KEY_MAP lookups in
  // libretro-core.js inject the correct keyboard keys. mupen64plus-next maps
  // RetroPad → N64 as: B→A, Y→B, A→C-Down, X→C-Up, L→C-Left, R→C-Right,
  // L2→Z, R2→R-shoulder, SELECT→L-shoulder (see emulate_game_controller_via_libretro.c).
  buttons: {
    B:      0x0001, // N64 A
    Y:      0x0002, // N64 B
    SELECT: 0x0004, // N64 L shoulder
    START:  0x0008, // Start
    // UP/DOWN/LEFT/RIGHT drive the ANALOG STICK on N64 (they inject WASD, which
    // retroarch.cfg binds to the analog axes). Sources: touch joystick + gamepad
    // left stick. The real N64 D-pad lives on DPAD_* below.
    UP:     0x0010,
    DOWN:   0x0020,
    LEFT:   0x0040,
    RIGHT:  0x0080,
    A:      0x0100, // N64 C-Down
    X:      0x0200, // N64 C-Up
    L:      0x0400, // N64 C-Left
    R:      0x0800, // N64 C-Right
    L2:     0x1000, // N64 Z trigger
    R2:     0x2000, // N64 R shoulder
    // True N64 D-pad (gamepad d-pad). Injects the arrow keys, which retroarch.cfg
    // binds to the N64 d-pad — kept distinct from the analog UP/DOWN/LEFT/RIGHT
    // above so the pad's stick and d-pad don't collapse together. High bits:
    // solo-injection only (never sent over the 16-bit lockstep/netplay path).
    DPAD_UP:    0x4000,
    DPAD_DOWN:  0x8000,
    DPAD_LEFT:  0x10000,
    DPAD_RIGHT: 0x20000,
  },
  keymap: {
    'KeyW':       0x0010, // D-Pad Up
    'KeyS':       0x0020, // D-Pad Down
    'KeyA':       0x0040, // D-Pad Left
    'KeyD':       0x0080, // D-Pad Right
    'ArrowUp':    0x4000,  // true N64 D-pad (distinct from analog WASD)
    'ArrowDown':  0x8000,
    'ArrowLeft':  0x10000,
    'ArrowRight': 0x20000,
    'KeyJ':       0x0001, // A   (RetroPad B)
    'KeyU':       0x0002, // B   (RetroPad Y)
    'KeyI':       0x0200, // C-Up    (RetroPad X)
    'KeyK':       0x0100, // C-Down  (RetroPad A)
    'KeyQ':       0x0400, // C-Left  (RetroPad L)
    'KeyE':       0x0800, // C-Right (RetroPad R)
    'Space':      0x1000, // Z trigger  (RetroPad L2)
    'KeyR':       0x2000, // R shoulder (RetroPad R2)
    'ShiftRight': 0x0004, // L shoulder (RetroPad SELECT)
    'Enter':      0x0008, // START
  },
  gamepadMap: {
    0:  0x0001, // A      → N64 A
    1:  0x0002, // B      → N64 B
    2:  0x0100, // X      → C-Down (also on the d-pad)
    3:  0x0200, // Y      → C-Up   (also on the d-pad)
    4:  0x0004, // LB     → N64 L shoulder
    5:  0x2000, // RB     → N64 R shoulder
    6:  0x1000, // LT     → N64 Z trigger
    7:  0x1000, // RT     → N64 Z trigger (mirrored on both triggers)
    9:  0x0008, // Start
    12: 0x0200, // D-pad up    → C-Up
    13: 0x0100, // D-pad down  → C-Down
    14: 0x0400, // D-pad left  → C-Left
    15: 0x0800, // D-pad right → C-Right
  },
  // Right analog stick → the N64 D-pad. The four C-buttons live on the
  // physical d-pad (above) — they're digital on real N64 hardware, so discrete
  // buttons fit them best. The rarely-used N64 d-pad rides the right stick.
  // Standard-gamepad axes 2 (X) / 3 (Y). Read by InputHandler._pollGamepad.
  rightStickMap: {
    xMinus: 0x10000, // D-pad Left  (DPAD_LEFT)
    xPlus:  0x20000, // D-pad Right (DPAD_RIGHT)
    yMinus: 0x4000,  // D-pad Up    (DPAD_UP)
    yPlus:  0x8000,  // D-pad Down  (DPAD_DOWN)
  },
  controls: [
    { label: 'Move / Turn (Stick)', keys: 'W A S D' },
    { label: 'D-Pad', keys: 'Arrow Keys' },
    { label: 'A / B', keys: 'J / U' },
    { label: 'C-Buttons (U/D/L/R)', keys: 'I / K / Q / E' },
    { label: 'Z / R / L', keys: 'Space / R / R-Shift' },
    { label: 'Start', keys: 'Enter' },
  ],
};

// Registry: id → SystemInfo
export const SYSTEMS = {
  atari2600: ATARI2600_SYSTEM,
  c64: C64_SYSTEM,
  nes: NES_SYSTEM,
  genesis: GENESIS_SYSTEM,
  gb: GB_SYSTEM,
  gbc: GB_SYSTEM, // alias — gambatte handles both .gb and .gbc
  snes: SNES_SYSTEM,
  gba: GBA_SYSTEM,
  n64: N64_SYSTEM,
};

// Non-zip ROM extensions for auto-detection by file extension
const NATIVE_EXTENSIONS = Object.values(SYSTEMS)
  .flatMap(s => s.extensions.filter(e => e !== '.zip'));

/**
 * Detect system from a filename's extension.
 * Returns the SystemInfo or null if unrecognized.
 * For .zip files, returns null (caller must check contents or use current system).
 */
// Extensions shared by multiple systems — ambiguous, so don't auto-switch.
const AMBIGUOUS_EXTENSIONS = new Set(['.bin', '.zip']);

export function detectSystem(filename) {
  const ext = filename.substring(filename.lastIndexOf('.')).toLowerCase();
  if (AMBIGUOUS_EXTENSIONS.has(ext)) return null;
  for (const sys of Object.values(SYSTEMS)) {
    if (sys.extensions.includes(ext)) return sys;
  }
  return null;
}
